// Global request deduplication system
window.GiftPackagingRequestManager = window.GiftPackagingRequestManager || {
  pendingRequests: new Map(),
  isProcessing: false,

  async makeRequest(requestKey, requestFunction) {
    if (this.pendingRequests.has(requestKey)) {
      return this.pendingRequests.get(requestKey);
    }

    const requestPromise = this.executeRequest(requestFunction);
    this.pendingRequests.set(requestKey, requestPromise);

    try {
      const result = await requestPromise;
      return result;
    } finally {
      this.pendingRequests.delete(requestKey);
    }
  },

  async executeRequest(requestFunction) {
    if (this.isProcessing) {
      await new Promise((resolve) => {
        const checkProcessing = () => {
          if (!this.isProcessing) {
            resolve();
          } else {
            requestAnimationFrame(checkProcessing);
          }
        };
        checkProcessing();
      });
    }

    this.isProcessing = true;
    try {
      const result = await requestFunction();
      return result;
    } finally {
      this.isProcessing = false;
    }
  },
};

class GiftPackagingSelector extends HTMLElement {
  constructor() {
    super();

    this.miniCart = document.querySelector('mini-cart');
    this.giftWrapping = this.dataset.giftWrapping;
    this.cartItemsSize = parseInt(this.getAttribute('cart-items-size'));
    this.giftWrapsInCart = parseInt(this.getAttribute('gift-wraps-in-cart'));
    this.itemsInCart = parseInt(this.getAttribute('items-in-cart'));

    this.transitionEndHandlers = new Map();
    this.isUpdating = false;
    this.requestQueue = [];
    this.isProcessingQueue = false;
    this.lastCartUpdateTime = 0;

    this.initializeDrawer();
    this.bindEvents();
    this.updateTriggerButton();
    this.bindRemoveButtonEvents();

    this.cartUpdateHandler = debounce(() => {
      this.updateTriggerButton();
      this.handleCartUpdate();
    }, 1000);
    document.addEventListener('cart:updated', this.cartUpdateHandler);
  }

  initializeDrawer() {
    this.drawer = document.querySelector('gift-packaging-drawer');
    this.selectedProductId = null;
    this.giftNoteEnabled = false;
    this.isAddingGiftPackaging = false;
    this.isUserInteracting = false;

    this.paperBagHandle = 'paper-bag-1';
    this.paperBagVariantIdHardcoded = '56931520217469';
    const paperBagEl = document.querySelector(`.gift-packaging-product[data-product-id="${this.paperBagVariantIdHardcoded}"]`);
    this.paperBagVariantId = paperBagEl ? paperBagEl.getAttribute('data-product-id') : this.paperBagVariantIdHardcoded;
  }

  async queueRequest(requestFunction) {
    return new Promise((resolve, reject) => {
      this.requestQueue.push({ requestFunction, resolve, reject });
      this.processQueue();
    });
  }

  async processQueue() {
    if (this.isProcessingQueue || this.requestQueue.length === 0) {
      return;
    }

    this.isProcessingQueue = true;

    while (this.requestQueue.length > 0) {
      const { requestFunction, resolve, reject } = this.requestQueue.shift();

      try {
        const result = await requestFunction();
        resolve(result);
      } catch (error) {
        reject(error);
      }

      // No delay needed - requests are already processed sequentially
    }

    this.isProcessingQueue = false;
  }

  bindEvents() {
    const triggerButton = this.querySelector('[data-gift-packaging-drawer-trigger]');
    if (triggerButton) {
      triggerButton.addEventListener('click', () => this.openDrawer());
    }

    const closeButtons = document.querySelectorAll('[data-gift-packaging-drawer-close]');
    closeButtons.forEach((button) => {
      button.addEventListener('click', () => this.closeDrawer());
    });

    if (this.drawer) {
      const overlay = this.drawer.querySelector('.gift-packaging-drawer__overlay');
      if (overlay) {
        overlay.addEventListener('click', () => this.closeDrawer());
      }
    }

    const productButtons = document.querySelectorAll('[data-select-product]');
    productButtons.forEach((button) => {
      button.addEventListener('click', (e) => this.selectProduct(e.target.dataset.selectProduct));
    });

    const addButton = document.querySelector('[data-add-gift-packaging]');
    if (addButton) {
      addButton.addEventListener('click', () => this.addGiftPackaging());
    }
  }

  bindRemoveButtonEvents() {
    document.addEventListener('click', (event) => {
      const removeButton = event.target.closest('cart-remove-button');
      if (!removeButton) return;

      const lineItemEl = removeButton.closest('[data-variant-id]');
      const handle = lineItemEl ? lineItemEl.getAttribute('data-handle') : null;
      const isNormalProduct = handle !== 'gift-wrapping' && handle !== 'paper-bag-1';

      const normalProductQuantity = this.calculateNormalProductQuantity();
      const isLastNormalProduct = normalProductQuantity <= 1;

      if (isNormalProduct && isLastNormalProduct) {
        event.preventDefault();
        this.handleCombinedRemoval(removeButton);
      }
    });
  }

  async handleCombinedRemoval(removeButton) {
    const itemIndex = removeButton.dataset.index;
    const cartItems = document.querySelector('cart-items');

    if (!cartItems || !itemIndex) {
      return;
    }

    const lineItemEl = removeButton.closest('[data-variant-id]');
    const variantId = lineItemEl ? lineItemEl.getAttribute('data-variant-id') : null;

    const normalProductQuantity = this.calculateNormalProductQuantity();
    const isLastNormalProduct = normalProductQuantity <= 1;

    if (isLastNormalProduct) {
      await this.removeNormalProductAndGiftPackaging({ itemIndex, variantId });
    } else {
      cartItems.updateQuantity(itemIndex, 0);
    }
  }

  async removeNormalProductAndGiftPackaging({ itemIndex, variantId }) {
    if (this.isUpdating) {
      return;
    }

    this.isUpdating = true;

    try {
      const cartItems = document.querySelector('cart-items');
      if (cartItems) {
        cartItems.enableLoading(itemIndex);
      }

      const requestKey = `remove-normal-and-gift-${variantId || itemIndex}-${Date.now()}`;
      const result = await window.GiftPackagingRequestManager.makeRequest(requestKey, async () => {
        const giftPackagingProducts = document.querySelectorAll('.gift-packaging-product');
        const giftProductIds = Array.from(giftPackagingProducts).map((product) => product.getAttribute('data-product-id'));

        const updates = {};

        if (variantId) {
          updates[variantId] = 0;
        } else {
          const lineEl = document.querySelector(`[data-index="${itemIndex}"]`)?.closest('[data-variant-id]');
          const resolvedId = lineEl ? lineEl.getAttribute('data-variant-id') : null;
          updates[resolvedId || itemIndex] = 0;
        }

        giftProductIds.forEach((id) => {
          updates[id] = 0;
        });

        const sections = this.getSectionsToRender().map((section) => section.section);

        const body = JSON.stringify({
          updates: updates,
          attributes: {
            'gift-wrapping': '',
          },
          sections: sections,
          sections_url: window.location.pathname,
        });

        const response = await fetch(`${theme.routes.cart_update_url}`, {
          ...fetchConfig(),
          ...{ body },
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        return await response.json();
      });

      if (result.errors) {
      }

      this.justDidCombinedRemoval = true;
      this.lastCartUpdateTime = Date.now();
      this.refreshCartWithResponse(result, true);
      this.updateTriggerButton();
    } catch (error) {
    } finally {
      const cartItems = document.querySelector('cart-items');
      if (cartItems) {
        cartItems.disableLoading();
      }
      this.isUpdating = false;
    }
  }

  handleImmediateGiftPackagingRemoval() {
    const giftPackagingProducts = document.querySelectorAll('.gift-packaging-product');
    const giftProductIds = Array.from(giftPackagingProducts).map((product) => product.getAttribute('data-product-id'));

    const normalProductQuantity = this.calculateNormalProductQuantity();

    if (normalProductQuantity <= 1) {
      giftProductIds.forEach((id) => {
        const cartItem = document.querySelector(`[data-variant-id="${id}"]`);
        if (cartItem) {
          cartItem.style.display = 'none';
          cartItem.style.opacity = '0';
          cartItem.style.transition = 'opacity 0.3s ease';
        }
      });

      const giftPackagingPlaceholder = this.querySelector('.gift-packaging-placeholder');
      if (giftPackagingPlaceholder) {
        giftPackagingPlaceholder.style.display = 'none';
      }

      this.updateTriggerButton();
    }
  }

  restoreGiftPackagingDisplay() {
    const giftPackagingProducts = document.querySelectorAll('.gift-packaging-product');
    const giftProductIds = Array.from(giftPackagingProducts).map((product) => product.getAttribute('data-product-id'));

    giftProductIds.forEach((id) => {
      const cartItem = document.querySelector(`[data-variant-id="${id}"]`);
      if (cartItem && cartItem.style.display === 'none') {
        cartItem.style.display = '';
        cartItem.style.opacity = '1';
        cartItem.style.transition = 'opacity 0.3s ease';
      }
    });

    const giftPackagingPlaceholder = this.querySelector('.gift-packaging-placeholder');
    if (giftPackagingPlaceholder && giftPackagingPlaceholder.style.display === 'none') {
      giftPackagingPlaceholder.style.display = 'block';
    }
  }

  openDrawer() {
    if (this.drawer) {
      this.drawer.classList.add('active');
      document.body.style.overflow = 'hidden';
      document.body.classList.add('gift-packaging-drawer--open');

      this.isUserInteracting = true;

      this.checkExistingGiftPackaging();
      this.updateTriggerButton();
      this.initializeGiftNoteState();

      if (!this.selectedProductId) {
        this.updateAddButton();
      }
    }
  }

  initializeGiftNoteState() {
    const giftNoteCheckbox = this.drawer?.querySelector('#gift-note-enabled-checkbox, #gift-note-enabled-checkbox-cart');
    const giftNoteTextarea = this.drawer?.querySelector('#gift-note-textarea, #gift-note-textarea-cart');

    if (giftNoteCheckbox && giftNoteTextarea) {
      if (this.selectedProductId) {
        const cartNote = this.dataset.cartNote;
        const hasCartNote = cartNote && cartNote.trim() !== '';
        const hasTextareaContent = giftNoteTextarea.value && giftNoteTextarea.value.trim() !== '';

        if (hasTextareaContent || hasCartNote) {
          this.giftNoteEnabled = true;
          giftNoteCheckbox.checked = true;
          this.toggleGiftNoteContainer(true);
        } else {
          giftNoteCheckbox.checked = false;
          this.giftNoteEnabled = false;
          this.toggleGiftNoteContainer(false);
        }
      } else {
        giftNoteCheckbox.checked = false;
        this.giftNoteEnabled = false;
        this.toggleGiftNoteContainer(false);
      }

      this.attachGiftNoteEventListeners(giftNoteCheckbox, giftNoteTextarea);
    }
  }

  attachGiftNoteEventListeners(giftNoteCheckbox, giftNoteTextarea) {
    if (this.giftNoteCheckboxHandler) {
      giftNoteCheckbox.removeEventListener('change', this.giftNoteCheckboxHandler);
    }
    if (this.giftNoteTextareaHandler) {
      giftNoteTextarea.removeEventListener('input', this.giftNoteTextareaHandler);
    }

    this.giftNoteCheckboxHandler = (e) => {
      this.giftNoteEnabled = e.target.checked;
      this.toggleGiftNoteContainer(e.target.checked);
      this.updateAddButton();

      if (!e.target.checked) {
        this.clearGiftNote();
      }
    };

    this.giftNoteTextareaHandler = debounce((e) => {
      if (this.giftNoteEnabled) {
        this.updateGiftNote(e.target.value);
      }
    }, 300);

    giftNoteCheckbox.addEventListener('change', this.giftNoteCheckboxHandler);
    giftNoteTextarea.addEventListener('input', this.giftNoteTextareaHandler);
  }

  checkExistingGiftPackaging() {
    const giftPackagingProducts = document.querySelectorAll('.gift-packaging-product');
    let foundGiftPackaging = false;
    let currentGiftProductId = null;

    for (const product of giftPackagingProducts) {
      const productId = product.getAttribute('data-product-id');
      const cartItem = document.querySelector(`[data-variant-id="${productId}"]`);

      if (cartItem) {
        const quantityInput = cartItem.querySelector('input[name="updates[]"]');
        const quantity = quantityInput ? parseInt(quantityInput.value) || 0 : 0;

        if (quantity > 0) {
          currentGiftProductId = productId;
          foundGiftPackaging = true;
          break;
        }
      }
    }

    this.selectedProductId = currentGiftProductId;
    this.updateProductSelection();
    this.updateAddButton();

    const giftNote = this.drawer?.querySelector('#gift-note-textarea, #gift-note-textarea-cart');
    const giftNoteCheckbox = this.drawer?.querySelector('#gift-note-enabled-checkbox, #gift-note-enabled-checkbox-cart');
    const cartNote = this.dataset.cartNote;
    const hasCartNote = cartNote && cartNote.trim() !== '';
    const hasTextareaContent = giftNote && giftNote.value && giftNote.value.trim() !== '';

    if (foundGiftPackaging && (hasTextareaContent || hasCartNote)) {
      this.giftNoteEnabled = true;
      if (giftNoteCheckbox) {
        giftNoteCheckbox.checked = true;
      }
      this.toggleGiftNoteContainer(true);
    } else {
      this.giftNoteEnabled = false;
      if (giftNoteCheckbox) {
        giftNoteCheckbox.checked = false;
      }
      this.toggleGiftNoteContainer(false);
    }

    if (!foundGiftPackaging) {
      this.selectedProductId = null;
      this.giftNoteEnabled = false;
      this.updateAddButton();

      const giftNoteCheckbox = this.drawer?.querySelector('#gift-note-enabled-checkbox, #gift-note-enabled-checkbox-cart');
      if (giftNoteCheckbox) {
        giftNoteCheckbox.checked = false;
      }
      const giftNoteTextarea = this.drawer?.querySelector('#gift-note-textarea, #gift-note-textarea-cart');
      if (giftNoteTextarea) {
        giftNoteTextarea.value = '';
      }
      this.toggleGiftNoteContainer(false);
    }
  }

  closeDrawer() {
    if (this.drawer) {
      this.drawer.classList.add('closing');
      this.drawer.classList.remove('active');

      const handleTransitionEnd = (event) => {
        if (event.target === this.drawer && event.propertyName === 'transform') {
          this.drawer.classList.remove('closing');
          document.body.style.overflow = '';
          document.body.classList.remove('gift-packaging-drawer--open');
          this.drawer.removeEventListener('transitionend', handleTransitionEnd);
          this.transitionEndHandlers.delete('drawer');

          this.resetDrawerState();
        }
      };

      this.transitionEndHandlers.set('drawer', handleTransitionEnd);
      this.drawer.addEventListener('transitionend', handleTransitionEnd);
    }
  }

  resetDrawerState() {
    this.selectedProductId = null;
    this.isUserInteracting = false;
  }

  closeDrawerWithCallback(callback) {
    if (this.drawer) {
      this.drawer.classList.add('closing');
      this.drawer.classList.remove('active');

      const handleTransitionEnd = (event) => {
        if (event.target === this.drawer && event.propertyName === 'transform') {
          this.drawer.classList.remove('closing');
          document.body.style.overflow = '';
          document.body.classList.remove('gift-packaging-drawer--open');
          this.drawer.removeEventListener('transitionend', handleTransitionEnd);
          this.transitionEndHandlers.delete('drawer');

          this.resetDrawerState();

          if (callback && typeof callback === 'function') {
            callback();
          }
        }
      };

      this.transitionEndHandlers.set('drawer', handleTransitionEnd);
      this.drawer.addEventListener('transitionend', handleTransitionEnd);
    }
  }

  selectProduct(productId) {
    this.selectedProductId = productId;
    this.updateProductSelection();
    this.updateAddButton();
  }

  updateProductSelection() {
    const products = document.querySelectorAll('.gift-packaging-product');
    products.forEach((product) => {
      const productId = product.dataset.productId;
      if (productId === this.selectedProductId) {
        product.classList.add('selected');
        product.querySelector('.gift-packaging-product__select').textContent = 'Selected';
        product.querySelector('.gift-packaging-product__select').disabled = true;
      } else {
        product.classList.remove('selected');
        product.querySelector('.gift-packaging-product__select').textContent = 'Select';
        product.querySelector('.gift-packaging-product__select').disabled = false;
      }
    });
  }

  updateAddButton() {
    const addButton = document.querySelector('[data-add-gift-packaging]');
    if (addButton) {
      addButton.disabled = !this.selectedProductId;

      const hasExistingGiftPackaging = this.hasExistingGiftPackaging();

      if (this.selectedProductId) {
        if (hasExistingGiftPackaging) {
          addButton.textContent = 'Add Gift Packaging';
        } else {
          addButton.textContent = 'Add Gift Packaging';
        }
      } else {
        addButton.textContent = 'Add Gift Packaging';
      }
    }
  }

  updateTriggerButton() {
    const triggerButton = this.querySelector('[data-gift-packaging-drawer-trigger]');
    if (triggerButton) {
      const hasExistingGiftPackaging = this.hasExistingGiftPackaging();
      triggerButton.textContent = hasExistingGiftPackaging ? 'Edit' : 'Add';
    }
  }

  async handleCartUpdate() {
    if (this.isAddingGiftPackaging) {
      return;
    }

    if (this.isUserInteracting) {
      return;
    }

    if (this.drawer && this.drawer.classList.contains('active')) {
      return;
    }

    if (this.isUpdating) {
      return;
    }

    if (this.justDidCombinedRemoval) {
      this.justDidCombinedRemoval = false;
      return;
    }

    if (this.justSyncedGiftPackaging) {
      this.justSyncedGiftPackaging = false;
      return;
    }

    const now = Date.now();
    if (now - this.lastCartUpdateTime < 100) {
      return;
    }
    this.lastCartUpdateTime = now;

    const hasExistingGiftPackaging = this.hasExistingGiftPackaging();
    const normalProductQuantity = this.calculateNormalProductQuantity();

    if (normalProductQuantity === 0 && hasExistingGiftPackaging) {
      await this.removeExistingGiftPackaging();
      return;
    }

    if (!hasExistingGiftPackaging) {
      const giftNoteCheckbox = this.drawer?.querySelector('#gift-note-enabled-checkbox, #gift-note-enabled-checkbox-cart');
      if (giftNoteCheckbox && giftNoteCheckbox.checked) {
        giftNoteCheckbox.checked = false;
        this.giftNoteEnabled = false;
        this.toggleGiftNoteContainer(false);

        const giftNoteTextarea = this.drawer?.querySelector('#gift-note-textarea, #gift-note-textarea-cart');
        if (giftNoteTextarea) {
          giftNoteTextarea.value = '';
        }
      }

      const giftPackagingPlaceholder = this.querySelector('.gift-packaging-placeholder');
      if (giftPackagingPlaceholder && giftPackagingPlaceholder.style.display === 'none') {
        giftPackagingPlaceholder.style.display = 'block';
      }
    } else {
      this.syncGiftPackagingQuantity();
    }
  }

  async syncGiftPackagingQuantity() {
    if (this.isUpdating) {
      return;
    }

    this.isUpdating = true;

    try {
      const normalProductQuantity = this.calculateNormalProductQuantity();

      if (normalProductQuantity === 0) {
        await this.removeExistingGiftPackaging();
        return;
      }

      const requestKey = `sync-gift-packaging-${normalProductQuantity}`;
      const result = await window.GiftPackagingRequestManager.makeRequest(requestKey, async () => {
        const giftPackagingProducts = document.querySelectorAll('.gift-packaging-product');
        const giftProductIds = Array.from(giftPackagingProducts).map((product) => product.getAttribute('data-product-id'));

        if (giftProductIds.length === 0) return null;

        const productsInCart = giftProductIds.filter((id) => {
          const cartItem = document.querySelector(`[data-variant-id="${id}"]`);
          if (!cartItem) return false;
          const quantityInput = cartItem.querySelector('input[name="updates[]"]');
          const qty = quantityInput ? parseInt(quantityInput.value) || 0 : 0;
          return qty > 0;
        });

        if (productsInCart.length === 0) return null;

        const currentGiftQuantity = this.getCurrentGiftPackagingQuantity();

        if (normalProductQuantity === currentGiftQuantity) {
          return null;
        }

        const updates = {};
        productsInCart.forEach((id) => {
          updates[id] = normalProductQuantity;
        });

        const sections = this.getSectionsToRender().map((section) => section.section);
        const body = JSON.stringify({
          updates: updates,
          sections: sections,
          sections_url: window.location.pathname,
        });

        const response = await fetch(`${theme.routes.cart_update_url}`, {
          ...fetchConfig(),
          ...{ body },
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        return await response.json();
      });

      if (result) {
        this.justSyncedGiftPackaging = true;
        this.lastCartUpdateTime = Date.now();
        this.refreshCartWithResponse(result, true);
      }
    } catch (error) {
    } finally {
      this.isUpdating = false;
    }
  }

  getCurrentGiftPackagingQuantity() {
    const giftPackagingProducts = document.querySelectorAll('.gift-packaging-product');
    const giftProductIds = Array.from(giftPackagingProducts).map((product) => product.getAttribute('data-product-id'));

    let maxGiftQuantity = 0;
    giftProductIds.forEach((id) => {
      const cartItem = document.querySelector(`[data-variant-id="${id}"]`);
      if (cartItem) {
        const quantityInput = cartItem.querySelector('input[name="updates[]"]');
        if (quantityInput) {
          const qty = parseInt(quantityInput.value) || 0;
          if (qty > maxGiftQuantity) maxGiftQuantity = qty;
        }
      }
    });

    return maxGiftQuantity;
  }

  updateGiftPackagingDisplay(quantity) {}

  removeGiftPackagingFromDisplay() {}

  updateCartTotals() {}

  calculateTotalItemCount() {
    const allCartItems = document.querySelectorAll('[data-variant-id]');
    let totalCount = 0;

    allCartItems.forEach((item) => {
      const quantityInput = item.querySelector('input[name="updates[]"]');
      if (quantityInput) {
        totalCount += parseInt(quantityInput.value) || 0;
      }
    });

    return totalCount;
  }

  refreshCartWithResponse(responseData, skipEvent = false) {
    const miniCart = document.querySelector('mini-cart');
    if (miniCart && miniCart.renderContents) {
      miniCart.renderContents(responseData);
    }

    const cartIconBubble = document.querySelector('cart-icon-bubble');
    if (cartIconBubble && cartIconBubble.renderContents) {
      cartIconBubble.renderContents(responseData);
    }

    const mobileCartIconBubble = document.querySelector('mobile-cart-icon-bubble');
    if (mobileCartIconBubble && mobileCartIconBubble.renderContents) {
      mobileCartIconBubble.renderContents(responseData);
    }

    const cartLiveRegionText = document.querySelector('cart-live-region-text');
    if (cartLiveRegionText && cartLiveRegionText.renderContents) {
      cartLiveRegionText.renderContents(responseData);
    }

    if (!skipEvent) {
      document.dispatchEvent(
        new CustomEvent('cart:updated', {
          detail: {
            cart: responseData,
          },
        })
      );

      if (typeof publish !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'gift-packaging', cartData: responseData });
      }
    }
  }

  async forceCartRefresh() {
    try {
      const sections = this.getSectionsToRender();
      const sectionsToFetch = sections.map((section) => section.section);

      const [cartResponse, ...sectionResponses] = await Promise.all([
        fetch(`${theme.routes.cart_url}.js`),
        ...sectionsToFetch.map((section) => fetch(`${theme.routes.cart_url}?section_id=${section}`).catch(() => null)),
      ]);

      const cartData = await cartResponse.json();

      const responseData = {
        ...cartData,
        sections: {},
      };

      const sectionPromises = sectionResponses.map(async (response, index) => {
        if (response) {
          try {
            const sectionHtml = await response.text();
            responseData.sections[sectionsToFetch[index]] = sectionHtml;
          } catch (sectionError) {}
        }
      });

      await Promise.all(sectionPromises);

      const miniCart = document.querySelector('mini-cart');
      if (miniCart && miniCart.renderContents) {
        miniCart.renderContents(responseData);
      }

      const cartIconBubble = document.querySelector('cart-icon-bubble');
      if (cartIconBubble && cartIconBubble.renderContents) {
        cartIconBubble.renderContents(responseData);
      }

      const mobileCartIconBubble = document.querySelector('mobile-cart-icon-bubble');
      if (mobileCartIconBubble && mobileCartIconBubble.renderContents) {
        mobileCartIconBubble.renderContents(responseData);
      }

      const cartLiveRegionText = document.querySelector('cart-live-region-text');
      if (cartLiveRegionText && cartLiveRegionText.renderContents) {
        cartLiveRegionText.renderContents(responseData);
      }

      document.dispatchEvent(
        new CustomEvent('cart:updated', {
          detail: {
            cart: cartData,
          },
        })
      );

      if (typeof publish !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'gift-packaging', cartData: cartData });
      }
    } catch (error) {}
  }

  calculateNormalProductQuantity() {
    const giftPackagingProducts = document.querySelectorAll('.gift-packaging-product');
    const giftProductIds = Array.from(giftPackagingProducts).map((product) => product.getAttribute('data-product-id'));

    const allCartItems = document.querySelectorAll('[data-variant-id]');
    let totalQuantity = 0;

    allCartItems.forEach((item) => {
      const variantId = item.getAttribute('data-variant-id');
      const handle = item.getAttribute('data-handle');
      const isGift = giftProductIds.includes(variantId);
      const isExcludedHandle = handle === 'gift-wrapping' || handle === 'paper-bag-1';
      if (!isGift && !isExcludedHandle) {
        const quantityInput = item.querySelector('input[name="updates[]"]');
        if (quantityInput) {
          totalQuantity += parseInt(quantityInput.value) || 0;
        }
      }
    });

    return totalQuantity;
  }

  hasExistingGiftPackaging() {
    const giftPackagingProducts = document.querySelectorAll('.gift-packaging-product');
    for (const product of giftPackagingProducts) {
      const productId = product.getAttribute('data-product-id');
      const cartItem = document.querySelector(`[data-variant-id="${productId}"]`);
      if (cartItem) {
        const quantityInput = cartItem.querySelector('input[name="updates[]"]');
        const qty = quantityInput ? parseInt(quantityInput.value) || 0 : 0;
        if (qty > 0) {
          return true;
        }
      }
    }
    return false;
  }

  toggleGiftNoteContainer(show) {
    const noteContainer = this.drawer?.querySelector('.gift-packaging-drawer__note-container');
    if (noteContainer) {
      if (show) {
        noteContainer.style.display = 'block';
        noteContainer.style.opacity = '1';
      } else {
        noteContainer.style.display = 'none';
        noteContainer.style.opacity = '0';
      }
    }
  }

  async updateGiftNote(noteText) {
    try {
      const requestKey = `update-gift-note-${noteText.length}`;
      await window.GiftPackagingRequestManager.makeRequest(requestKey, async () => {
        const body = JSON.stringify({
          note: noteText,
        });

        const response = await fetch(`${theme.routes.cart_update_url}`, {
          ...fetchConfig(),
          ...{ body },
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        return await response.json();
      });
    } catch (error) {}
  }

  async clearGiftNote() {
    try {
      const requestKey = `clear-gift-note-${Date.now()}`;
      await window.GiftPackagingRequestManager.makeRequest(requestKey, async () => {
        const body = JSON.stringify({
          note: '',
        });

        const response = await fetch(`${theme.routes.cart_update_url}`, {
          ...fetchConfig(),
          ...{ body },
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        return await response.json();
      });

      const giftNoteCheckbox = this.drawer?.querySelector('#gift-note-enabled-checkbox, #gift-note-enabled-checkbox-cart');
      if (giftNoteCheckbox) {
        giftNoteCheckbox.checked = false;
      }

      const giftNoteTextarea = this.drawer?.querySelector('#gift-note-textarea, #gift-note-textarea-cart');
      if (giftNoteTextarea) {
        giftNoteTextarea.value = '';
      }
    } catch (error) {}
  }

  async removeExistingGiftPackaging() {
    if (this.isUpdating) {
      return;
    }

    this.isUpdating = true;

    try {
      const requestKey = `remove-gift-packaging-${Date.now()}`;
      const result = await window.GiftPackagingRequestManager.makeRequest(requestKey, async () => {
        const giftPackagingProducts = document.querySelectorAll('.gift-packaging-product');
        const giftProductIds = Array.from(giftPackagingProducts).map((product) => product.getAttribute('data-product-id'));

        if (giftProductIds.length === 0) return null;

        const productsInCart = giftProductIds.filter((id) => {
          const cartItem = document.querySelector(`[data-variant-id="${id}"]`);
          if (!cartItem) return false;
          const quantityInput = cartItem.querySelector('input[name="updates[]"]');
          const qty = quantityInput ? parseInt(quantityInput.value) || 0 : 0;
          return qty > 0;
        });

        if (productsInCart.length === 0) return null;

        const updates = {};
        productsInCart.forEach((id) => {
          updates[id] = 0;
        });

        const sections = this.getSectionsToRender().map((section) => section.section);
        const body = JSON.stringify({
          updates: updates,
          attributes: {
            'gift-wrapping': '',
          },
          sections: sections,
          sections_url: window.location.pathname,
        });

        const response = await fetch(`${theme.routes.cart_update_url}`, {
          ...fetchConfig(),
          ...{ body },
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        return await response.json();
      });

      if (result) {
        this.lastCartUpdateTime = Date.now();
        this.refreshCartWithResponse(result, true);
      }
    } catch (error) {
    } finally {
      this.isUpdating = false;
    }
  }

  async addGiftPackaging() {
    if (!this.selectedProductId) return;

    const addButton = document.querySelector('[data-add-gift-packaging]');
    const originalText = addButton.textContent;
    addButton.textContent = 'Adding...';
    addButton.disabled = true;

    this.isAddingGiftPackaging = true;

    try {
      let giftNoteText = '';
      const giftNoteTextarea = this.drawer?.querySelector('#gift-note-textarea, #gift-note-textarea-cart');
      if (giftNoteTextarea) {
        giftNoteText = giftNoteTextarea.value.trim();
      }

      const requestKey = `add-gift-packaging-${this.selectedProductId}-${this.itemsInCart}`;
      const result = await window.GiftPackagingRequestManager.makeRequest(requestKey, async () => {
        const giftPackagingProducts = document.querySelectorAll('.gift-packaging-product');
        const giftProductIds = Array.from(giftPackagingProducts).map((product) => product.getAttribute('data-product-id'));

        const updates = {};
        giftProductIds.forEach((id) => {
          if (id !== this.paperBagVariantId) {
            updates[id] = 0;
          }
        });

        const currentNormalQty = this.calculateNormalProductQuantity();
        updates[this.selectedProductId] = currentNormalQty;

        const sections = this.getSectionsToRender().map((section) => section.section);
        const body = JSON.stringify({
          updates: updates,
          attributes: {
            'gift-wrapping': true,
          },
          note: giftNoteText,
          sections: sections,
          sections_url: window.location.pathname,
        });

        const response = await fetch(`${theme.routes.cart_update_url}`, {
          ...fetchConfig(),
          ...{ body },
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({ errors: 'Cart Error' }));
          throw new Error(errorData.errors || `HTTP error! status: ${response.status}`);
        }

        const result = await response.json();
        if (result.errors) {
          throw new Error(result.errors);
        }

        return result;
      });

      if (document.body.classList.contains('template-cart')) {
        window.location.href = theme.routes.cart_url;
        return;
      }

      this.closeDrawerWithCallback(() => {
        if (this.miniCart) {
          this.miniCart.renderContents(result);
        }
        this.updateTriggerButton();
        this.isAddingGiftPackaging = false;
      });
    } catch (error) {
      this.isAddingGiftPackaging = false;
    } finally {
      if (addButton) {
        addButton.textContent = originalText;
        addButton.disabled = false;
      }
    }
  }

  getSectionsToRender() {
    return [
      {
        id: 'mini-cart',
        section: 'mini-cart',
        selector: '.shopify-section',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
      {
        id: 'mobile-cart-icon-bubble',
        section: 'mobile-cart-icon-bubble',
        selector: '.shopify-section',
      },
      {
        id: 'cart-live-region-text',
        section: 'cart-live-region-text',
        selector: '.shopify-section',
      },
    ];
  }

  cleanup() {
    this.transitionEndHandlers.forEach((handler, key) => {
      if (key === 'drawer' && this.drawer) {
        this.drawer.removeEventListener('transitionend', handler);
      } else if (key === 'noteContainer') {
        const noteContainer = this.drawer?.querySelector('.gift-packaging-drawer__note-container');
        if (noteContainer) {
          noteContainer.removeEventListener('transitionend', handler);
        }
      }
    });
    this.transitionEndHandlers.clear();

    if (this.cartUpdateHandler) {
      document.removeEventListener('cart:updated', this.cartUpdateHandler);
      this.cartUpdateHandler = null;
    }

    if (this.giftNoteCheckboxHandler) {
      const giftNoteCheckbox = this.drawer?.querySelector('#gift-note-enabled-checkbox, #gift-note-enabled-checkbox-cart');
      if (giftNoteCheckbox) {
        giftNoteCheckbox.removeEventListener('change', this.giftNoteCheckboxHandler);
      }
      this.giftNoteCheckboxHandler = null;
    }

    if (this.giftNoteTextareaHandler) {
      const giftNoteTextarea = this.drawer?.querySelector('#gift-note-textarea, #gift-note-textarea-cart');
      if (giftNoteTextarea) {
        giftNoteTextarea.removeEventListener('input', this.giftNoteTextareaHandler);
      }
      this.giftNoteTextareaHandler = null;
    }
  }

  disconnectedCallback() {
    this.cleanup();
  }
}

class GiftPackagingDrawer extends HTMLElement {
  constructor() {
    super();
  }
}

customElements.define('gift-packaging-selector', GiftPackagingSelector);
customElements.define('gift-packaging-drawer', GiftPackagingDrawer);
